import { yupResolver } from '@hookform/resolvers/yup';
import type { CountryCode } from 'libphonenumber-js';
import { ParseError, parsePhoneNumberWithError } from 'libphonenumber-js';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ReCAPTCHA } from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Button } from '@/components/Button';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { TitleField } from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import { SelectField } from '@/components/SelectField';
import { TextAreaField } from '@/components/TextAreaField';
import { Typography } from '@/components/Typography';
import { watchAvailGuest } from '@/lib/availability/helper';
import {
  formatNationalByPhonePrefix,
  getExamplePhone,
  isValidPhoneNumber,
  parsePhoneNumber,
} from '@/lib/phone';
import { getPhonePrefixSelectOpts } from '@/lib/phonePrefixes/helpers';
import { commonMessages } from '@/messages/common';
import { contactFormMessages } from '@/messages/contactForm';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import { useRetailerPageContext } from '@/providers/RetailerPageProvider/RetailerPageProviderContext';

import { isRetailerContactUsField } from './helpers';
import type { RetailerContactUsFormValues } from './schema';
import { contactUsSchema } from './schema';

import * as S from './RetailerContactUs.styles';

interface RetailerContactUsProps {
  productName: string;
  retailerId?: string;
  sku: string;
}

export const RetailerContactUs: FC<RetailerContactUsProps> = ({
  productName,
  retailerId,
  sku,
}) => {
  const { formatMessage } = useIntl();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [examplePhone, setExamplePhone] = useState<string>('');
  const [isVerifyingRecaptcha, setIsVerifyingRecaptcha] = useState(false);

  const { handleDiscoverCollection } = useRetailerPageContext();

  const {
    country,
    locale: { sfCode },
    query: { languageCode },
  } = useRegionProvider();

  const reCaptchaRef = useRef<ReCAPTCHA>(null);

  const schema = contactUsSchema(formatMessage, country);

  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });
  const captcha = formCtx.watch('captcha');

  const phonePrefix = formCtx.watch('phonePrefix');
  const phone = formCtx.watch('phone');
  useEffect(() => {
    if (phone && phonePrefix?.value) {
      try {
        const phoneNumber = parsePhoneNumberWithError(
          phone,
          phonePrefix.code as CountryCode,
        );
        formCtx.setValue(
          'phone',
          formatNationalByPhonePrefix(
            phonePrefix.value,
            phoneNumber.formatNational(),
          ),
        );

        formCtx.clearErrors('phone');
      } catch (error) {
        if (error instanceof ParseError) {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.invalidPhoneNumber),
          });
        } else {
          formCtx.setError('phone', {
            message: formatMessage(formMessages.technicalError),
          });
        }
      }
    }
  }, [phone, phonePrefix, formCtx, formatMessage]);

  useEffect(() => {
    const dataPhone = formCtx.watch('phone');
    const dataPhonePrefix = formCtx.watch('phonePrefix');

    if (!dataPhonePrefix?.value || !dataPhonePrefix.code) return;

    const fullPhoneNumber = dataPhonePrefix?.value + dataPhone;
    const isValidPhone = isValidPhoneNumber(fullPhoneNumber);
    if (isValidPhone) {
      const phoneNumber = parsePhoneNumber(fullPhoneNumber);
      formCtx.setValue(
        'phone',
        formatNationalByPhonePrefix(
          dataPhonePrefix.value,
          phoneNumber?.formatNational(),
        ),
      );
    }
    const examplePhoneNumber = getExamplePhone(
      dataPhonePrefix.code as CountryCode,
    );
    if (examplePhoneNumber) setExamplePhone(examplePhoneNumber);
  }, [formCtx.watch('phone'), formCtx.watch('phonePrefix')]);

  if (!retailerId) {
    return null;
  }

  const handleSubmit: SubmitHandler<RetailerContactUsFormValues> = async ({
    email,
    firstName,
    lastName,
    message,
    phone,
    phonePrefix,
    title,
  }) => {
    const params = {
      boutiqueId: retailerId,
      email,
      firstName,
      languageCode: sfCode,
      lastName,
      comments: message,
      productSku: sku,
      salutation: title?.value ?? '',
      websiteCountryCode: country.code,
      ...(phone && {
        phone: phonePrefix.value + phone,
      }),
      newsletterOptIn: false,
      recaptchaResponse: reCaptchaRef.current?.getValue() ?? '',
      source: 'Widget',
    };

    setProcessing(true);
    try {
      const res = await watchAvailGuest(params);
      setProcessing(false);
      if (res.code === 200) {
        setIsSubmitted(true);
      } else {
        setProcessing(false);

        for (const field of res.invalid) {
          if (!isRetailerContactUsField(field) || !field) {
            setError(formatMessage({ id: 'genericError' }));
            continue;
          }

          const fieldSchema = schema.fields[field];
          if ((fieldSchema as any)?.type === 'ref') continue;

          setError(formatMessage({ id: 'genericError' }));
          formCtx.setError(field, {
            type: 'manual',
            message: formatMessage(formMessages.invalidField),
          });
        }
        setError(formatMessage({ id: 'genericError' }));
      }
    } catch (error) {
      setProcessing(false);
      setError(
        formatMessage({
          id: 'genericError',
        }),
      );
    }
  };

  if (isSubmitted) {
    return (
      <S.FormWrapper>
        <S.FormRow>
          <Col md={12} style={{ textAlign: 'center' }}>
            <Typography marginBottom={4} scale='h1'>
              {formatMessage(contactFormMessages.title)}
            </Typography>

            <Typography marginBottom={4} scale='p2'>
              <div style={{ fontWeight: 'bold' }}>
                {formatMessage(contactFormMessages.thankYouTitle)}
              </div>
              <br />
              {formatMessage(contactFormMessages.thankYouMessage, {
                productName: productName,
              })}
            </Typography>
          </Col>
          <Col md={12} style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              disabled={processing}
              onClick={handleDiscoverCollection}
              type='button'
            >
              {formatMessage(contactFormMessages.cta)}
            </Button>
          </Col>
        </S.FormRow>
      </S.FormWrapper>
    );
  }

  return (
    <FormProvider
      name='campaign-subscription-form'
      {...formCtx}
      onSubmit={handleSubmit}
    >
      <S.FormWrapper>
        <S.FormRow>
          <Col md={12} style={{ textAlign: 'center' }}>
            <Typography marginBottom={4} scale='h1'>
              {formatMessage(contactFormMessages.title)}
            </Typography>
            <Typography marginBottom={4} scale='p2'>
              {formatMessage(contactFormMessages.description)}
            </Typography>
          </Col>

          <Col
            md={12}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <S.FieldRow mb={2}>
              <Col md={6}>
                <TitleField colorVariant='grey' countryCode={country.code} />
              </Col>
            </S.FieldRow>

            <S.FieldRow>
              <>
                <Col md={6}>
                  <InputField
                    colorVariant='grey'
                    name='firstName'
                    placeholder={formatMessage(formMessages.firstName)}
                    required
                  />
                </Col>

                <Col md={6}>
                  <InputField
                    colorVariant='grey'
                    name='lastName'
                    placeholder={formatMessage(formMessages.lastName)}
                    required
                  />
                </Col>

                <Col md={6}>
                  <InputField
                    autoComplete='email'
                    colorVariant='grey'
                    name='email'
                    placeholder={formatMessage(formMessages.email)}
                    required
                  />
                </Col>

                <Col md={3} xs={6}>
                  <SelectField
                    aria-label={formatMessage(formMessages.phone)}
                    name='phonePrefix'
                    options={getPhonePrefixSelectOpts()}
                    phonePrefixDisplayFormat='short'
                  />
                </Col>

                <Col md={3} xs={6}>
                  <Flex flexDirection='column'>
                    <InputField
                      autoComplete='phone'
                      name='phone'
                      pattern='[0-9]+'
                      placeholder={formatMessage(formMessages.telephoneNumber)}
                      required
                      type='text'
                    />
                    {examplePhone && (
                      <Typography
                        color='secondaryGrey'
                        scale='p3'
                        textAlign='right'
                      >
                        {examplePhone}
                      </Typography>
                    )}
                  </Flex>
                </Col>

                <Col md={12}>
                  <TextAreaField
                    colorVariant='grey'
                    name='message'
                    placeholder={formatMessage(
                      contactFormMessages.messagePlaceholder,
                    )}
                    required
                    style={{ height: '100px' }}
                  />
                </Col>

                <Col md={12}>
                  <Flex alignItems='center' justifyContent='flex-end'>
                    <Typography color='secondaryGrey' scale='p3'>
                      {formatMessage(formMessages.mandatoryFields)}
                    </Typography>
                  </Flex>
                </Col>

                <Col md={12}>
                  <ReCaptcha
                    ref={reCaptchaRef}
                    languageCode={languageCode}
                    name='captcha'
                    onChange={setIsVerifyingRecaptcha}
                    validation={false}
                  />
                </Col>
                {error && (
                  <Col md={12}>
                    <Typography color='error' textAlign='center'>
                      {error}
                    </Typography>
                  </Col>
                )}
                <Col
                  md={12}
                  style={{ display: 'flex', justifyContent: 'center' }}
                >
                  <LegacyButton
                    disabled={processing || !captcha || isVerifyingRecaptcha}
                    isProcessing={processing || isVerifyingRecaptcha}
                    type='submit'
                  >
                    {formatMessage(commonMessages.submit)}
                  </LegacyButton>
                </Col>
              </>
            </S.FieldRow>
          </Col>
        </S.FormRow>
      </S.FormWrapper>
    </FormProvider>
  );
};
